import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type AssetUsageResult, type ListAssetLibraryResult } from '@whinerp/client';
import { BLOCK_PALETTES, type BlockDocument } from '@whinerp/contracts/blocks';
import { formatCount } from '@whinerp/contracts/format';
import { BlockEditor, Button, EmptyState, ErrorState, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { blockEditorLabels } from './block-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-65 Asset library (F-084).
 *
 * A folder tree, a search bar and a grid of what is in the library, with a
 * thumbnail for every image. Choosing an asset shows where it is used before
 * anything else - and removing one that is used is not offered, because the
 * server refuses it: an image live in three campaigns is prevented from being
 * deleted, not merely warned about.
 */

type Library = ListAssetLibraryResult;
type Asset = Library['assets'][number];
type Kind = Asset['kind'];
const KINDS: Kind[] = ['image', 'document', 'snippet', 'template'];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function AssetLibraryScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const manage = can('campaign.send');

  const [folderId, setFolderId] = useState<string | null>(null);
  const [kind, setKind] = useState<Kind | ''>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const library = useQuery<Library, ApiError>({
    queryKey: ['asset-library', folderId, kind, search],
    queryFn: () =>
      api.listAssetLibrary({
        query: { ...(folderId ? { folderId } : {}), ...(kind ? { kind } : {}), ...(search.trim() ? { search: search.trim() } : {}) },
      }),
    placeholderData: (previous) => previous,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['asset-library'] });

  if (library.isPending) return <Skeleton className="h-plot w-full" />;
  if (library.isError) return <ErrorState title={t('state.error')} body={errorText(library.error)} onRetry={() => void library.refetch()} />;

  const { folders, assets, roles } = library.data;
  const folder = folders.find((entry) => entry.folderId === folderId) ?? null;
  const chosen = assets.find((asset) => asset.assetId === selected) ?? null;
  const canAddHere = manage && (folder === null || folder.canEdit);

  const tree = (parentId: string | null, depth: number): JSX.Element[] =>
    folders
      .filter((entry) => entry.parentId === parentId)
      .flatMap((entry) => [
        <li key={entry.folderId}>
          <button
            type="button"
            data-asset-folder={entry.name}
            aria-current={folderId === entry.folderId ? 'true' : undefined}
            onClick={() => setFolderId(entry.folderId)}
            style={{ paddingInlineStart: `${String(depth * 12)}px` }}
            className="w-full rounded-control px-2 py-1 text-start text-dense text-ink hover:bg-canvas aria-[current]:bg-bronze-tint"
          >
            {entry.name}
            {entry.editorRoleName ? <span className="ms-1 text-meta text-ink-meta">({entry.editorRoleName})</span> : null}
          </button>
        </li>,
        ...tree(entry.folderId, depth + 1),
      ]);

  return (
    <div data-component="AssetLibrary" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{t('assets.library')}</PageHeading>
        <input
          type="search"
          data-asset-search
          aria-label={t('assets.search')}
          placeholder={t('assets.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={`${INPUT} ms-auto w-full max-w-reading sm:w-auto`}
        />
        <select data-asset-kind aria-label={t('assets.kind')} value={kind} onChange={(event) => setKind(event.target.value as Kind | '')} className={INPUT}>
          <option value="">{t('assets.kindAll')}</option>
          {KINDS.map((option) => (
            <option key={option} value={option}>{t(`assets.kind.${option}`)}</option>
          ))}
        </select>
      </header>

      <div className="flex flex-col gap-4 lg:flex-row">
        <aside className="flex w-full shrink-0 flex-col gap-2 lg:w-panel-narrow">
          <Panel data-region="asset-folders">
            <nav aria-label={t('assets.folders')} className="flex flex-col gap-1">
              <SectionLabel>{t('assets.folders')}</SectionLabel>
              <ul className="flex flex-col">
                <li>
                  <button
                    type="button"
                    data-asset-folder-all
                    aria-current={folderId === null ? 'true' : undefined}
                    onClick={() => setFolderId(null)}
                    className="w-full rounded-control px-2 py-1 text-start text-dense text-ink hover:bg-canvas aria-[current]:bg-bronze-tint"
                  >
                    {t('assets.all')}
                  </button>
                </li>
                {tree(null, 0)}
              </ul>
            </nav>
          </Panel>
          {manage ? <NewFolder parentId={folderId} roles={roles} onDone={refresh} /> : null}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {folder && !folder.canEdit ? (
            <p data-asset-locked className="text-meta text-ink-meta">{t('assets.locked', { values: { role: folder.editorRoleName ?? '' } })}</p>
          ) : null}
          {assets.length === 0 ? (
            <EmptyState title={t('assets.none')} body={t('assets.noneBody')} primary={{ label: t('assets.add'), href: '/assets#asset-add', icon: 'Plus' }} />
          ) : (
            <ul data-asset-grid className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {assets.map((asset) => (
                <li key={asset.assetId}>
                  <button
                    type="button"
                    data-asset={asset.name}
                    aria-pressed={selected === asset.assetId}
                    onClick={() => setSelected(asset.assetId)}
                    className="flex w-full flex-col gap-1 rounded-card border border-rule bg-surface p-2 text-start hover:border-bronze aria-pressed:border-bronze"
                  >
                    {asset.kind === 'image' && asset.url ? (
                      <img src={asset.url} alt={asset.name} className="h-block-sm w-full rounded-control object-contain" />
                    ) : (
                      <span className="flex h-block-sm w-full items-center justify-center rounded-control bg-canvas text-meta text-ink-meta">
                        {t(`assets.kind.${asset.kind}`)}
                      </span>
                    )}
                    <span className="truncate text-dense text-ink">{asset.name}</span>
                    <span data-asset-uses className="text-meta text-ink-meta">
                      {asset.usageCount > 0
                        ? t('assets.usedIn', { values: { count: formatCount(asset.usageCount, format) } })
                        : t('assets.unused')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {chosen ? <AssetDetail key={chosen.assetId} asset={chosen} folders={library.data.folders} onDone={() => { setSelected(null); void refresh(); }} /> : null}
          {canAddHere ? <AddAsset folderId={folderId} onDone={refresh} /> : null}
        </div>
      </div>
    </div>
  );
}

function NewFolder({ parentId, roles, onDone }: { parentId: string | null; roles: Library['roles']; onDone: () => void }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const create = useMutation({
    mutationFn: () => api.createAssetFolder({ body: { name, parentId, editorRoleId: roleId || null } }),
    onSuccess: () => {
      setName('');
      setRoleId('');
      onDone();
    },
  });
  return (
    <Panel data-region="asset-new-folder">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('assets.newFolder')}</SectionLabel>
        <input data-asset-folder-name aria-label={t('assets.folderName')} placeholder={t('assets.folderName')} value={name} onChange={(event) => setName(event.target.value)} className={INPUT} />
        {roles.length > 0 ? (
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('assets.editorRole')}
            <select data-asset-folder-role value={roleId} onChange={(event) => setRoleId(event.target.value)} className={INPUT}>
              <option value="">{t('assets.anyoneEdits')}</option>
              {roles.map((role) => (
                <option key={role.roleId} value={role.roleId}>{role.name}</option>
              ))}
            </select>
          </label>
        ) : null}
        {create.error ? <p className="text-dense text-danger-text">{errorText(create.error)}</p> : null}
        <div>
          <Button intent="secondary" size="small" data-asset-folder-create disabled={create.isPending} onClick={() => create.mutate()}>
            {t('assets.createFolder')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function AddAsset({ folderId, onDone }: { folderId: string | null; onDone: () => void }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const [kind, setKind] = useState<Kind>('image');
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [body, setBody] = useState('');
  const [document, setDocument] = useState<BlockDocument>({ blocks: [] });
  const add = useMutation({
    mutationFn: async () => {
      const content = file && (kind === 'image' || kind === 'document') ? toBase64(new Uint8Array(await file.arrayBuffer())) : null;
      return api.addAsset({
        body: {
          folderId,
          kind,
          name: name.trim() || file?.name || '',
          tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
          filename: file?.name ?? null,
          contentType: file?.type ?? null,
          contentBase64: content,
          body: kind === 'snippet' ? body : null,
          blocks: kind === 'template' ? document.blocks : null,
        },
      });
    },
    onSuccess: () => {
      setName('');
      setTags('');
      setFile(null);
      setBody('');
      setDocument({ blocks: [] });
      onDone();
    },
  });
  return (
    <Panel data-region="asset-add" id="asset-add">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('assets.add')}</SectionLabel>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('assets.kind')}
            <select data-asset-add-kind value={kind} onChange={(event) => setKind(event.target.value as Kind)} className={INPUT}>
              {KINDS.map((option) => (
                <option key={option} value={option}>{t(`assets.kind.${option}`)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('assets.name')}
            <input data-asset-add-name value={name} onChange={(event) => setName(event.target.value)} className={INPUT} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('assets.tags')}
            <input data-asset-add-tags value={tags} onChange={(event) => setTags(event.target.value)} className={INPUT} />
          </label>
        </div>
        {kind === 'image' || kind === 'document' ? (
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('assets.file')}
            <input
              type="file"
              data-asset-add-file
              accept={kind === 'image' ? 'image/png,image/jpeg,image/gif,image/webp' : undefined}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="text-dense text-ink"
            />
          </label>
        ) : null}
        {kind === 'snippet' ? (
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('assets.snippetText')}
            <textarea data-asset-add-body rows={4} value={body} onChange={(event) => setBody(event.target.value)} className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink" />
          </label>
        ) : null}
        {kind === 'template' ? (
          <BlockEditor palette={BLOCK_PALETTES['landing-page']} value={document} onChange={setDocument} labels={blockEditorLabels(t)} />
        ) : null}
        {add.error ? <p data-asset-add-refusal className="text-dense text-danger-text">{errorText(add.error)}</p> : null}
        <div>
          <Button intent="primary" data-asset-add-save disabled={add.isPending} onClick={() => add.mutate()}>
            {t('assets.save')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function AssetDetail({ asset, folders, onDone }: { asset: Asset; folders: Library['folders']; onDone: () => void }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const { can } = useSession();
  const manage = can('campaign.send') && asset.canEdit;
  const [name, setName] = useState(asset.name);
  const [tags, setTags] = useState(asset.tags.join(', '));
  const [folderId, setFolderId] = useState(asset.folderId ?? '');
  const usage = useQuery<AssetUsageResult, ApiError>({
    queryKey: ['asset-usage', asset.assetId],
    queryFn: () => api.assetUsage({ path: { assetId: asset.assetId } }),
    enabled: can('campaign.send'),
  });
  const save = useMutation({
    mutationFn: () =>
      api.updateAsset({
        path: { assetId: asset.assetId },
        body: { name, tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean), folderId: folderId || null },
      }),
    onSuccess: onDone,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteAsset({ path: { assetId: asset.assetId }, body: {} }),
    onSuccess: onDone,
  });
  const refusal = save.error ?? remove.error;
  const movable = folders.filter((entry) => entry.canEdit);

  return (
    <Panel data-region="asset-detail" data-asset-detail={asset.name}>
      <div className="flex flex-col gap-3">
        <SectionLabel>{asset.name}</SectionLabel>
        {asset.url ? (
          <p className="break-all text-meta text-ink-meta">
            {t('assets.address')} <span data-asset-url>{asset.url}</span>
          </p>
        ) : null}
        {asset.kind === 'snippet' && asset.body ? <p className="whitespace-pre-wrap text-dense text-ink">{asset.body}</p> : null}

        <div className="flex flex-col gap-1">
          <SectionLabel>{t('assets.whereUsed')}</SectionLabel>
          {usage.isPending && can('campaign.send') ? (
            <Skeleton className="h-block-sm w-full" />
          ) : usage.data && usage.data.uses.length > 0 ? (
            <ul data-asset-usage className="flex flex-col">
              {usage.data.uses.map((use) => (
                <li key={use.documentId} className="text-dense text-ink">
                  {use.kind === 'email' ? (
                    <Link to={`/emails/${use.documentId}/edit`} className="text-bronze-text hover:text-ink">{use.where ?? use.name}</Link>
                  ) : (
                    use.where ?? use.name
                  )}{' '}
                  <span className="text-meta text-ink-meta">{t(`assets.use.${use.kind}`)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p data-asset-unused className="text-meta text-ink-meta">{t('assets.unused')}</p>
          )}
        </div>

        {manage ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('assets.name')}
              <input data-asset-edit-name value={name} onChange={(event) => setName(event.target.value)} className={INPUT} />
            </label>
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('assets.tags')}
              <input data-asset-edit-tags value={tags} onChange={(event) => setTags(event.target.value)} className={INPUT} />
            </label>
            {movable.length > 0 ? (
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('assets.folder')}
                <select data-asset-edit-folder value={folderId} onChange={(event) => setFolderId(event.target.value)} className={INPUT}>
                  <option value="">{t('assets.noFolder')}</option>
                  {movable.map((entry) => (
                    <option key={entry.folderId} value={entry.folderId}>{entry.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        ) : null}
        {refusal ? <p data-asset-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
        {manage ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button intent="secondary" size="small" data-asset-edit-save disabled={save.isPending} onClick={() => save.mutate()}>
              {t('assets.save')}
            </Button>
            <Button
              intent="ghost"
              size="small"
              data-asset-delete
              disabled={remove.isPending || asset.usageCount > 0}
              title={asset.usageCount > 0 ? t('assets.deleteBlocked') : undefined}
              onClick={() => remove.mutate()}
            >
              {t('assets.delete')}
            </Button>
            {asset.usageCount > 0 ? <span data-asset-delete-blocked className="text-meta text-ink-meta">{t('assets.deleteBlocked')}</span> : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

function LibraryLoading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function LibraryError(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('assets.none')} />;
}
function LibraryEmpty(): JSX.Element {
  const t = useT();
  return <EmptyState title={t('assets.none')} body={t('assets.noneBody')} primary={{ label: t('assets.add'), href: '/assets#asset-add', icon: 'Plus' }} />;
}

export const screen = defineScreen({
  id: 'SC-65',
  route: '/assets',
  surface: 'page',
  title: 'Asset library',
  features: ['F-084'],
  entitlement: 'F-084',
  reachedBy: [{ kind: 'screen', from: 'SC-16', how: 'the asset library link on the campaigns list' }],
  components: [],
  elements: ['AssetLibrary'],
  states: { empty: LibraryEmpty, loading: LibraryLoading, error: LibraryError },
  component: AssetLibraryScreen,
});

export default screen;
